import { defineStore } from "pinia";
import { ref } from "vue";
import { User } from "@/type/api";
import { SSEManager, type SSEMessageResource } from "@/SSEManager";
import { useSessionStore } from "@/store/session";

export const useUserStore = defineStore("users", () => {
	// State
	// The list is cached per scope: the district's own members (the default
	// every ordinary surface reads) and, for an administrator who asks for it,
	// every user in the system. Keeping them apart means loading the
	// cross-organization list for organization configuration or sudo does not
	// change what Operations, Reveal, or the roster show.
	const _allByScope = ref<Map<boolean, User[]>>(new Map());
	const _byID = ref<Map<number, User>>(new Map());
	const error = ref(null);
	const loading = ref(false);
	const ongoingFetches = new Map<boolean, Promise<User[]>>();

	// Subscription
	SSEManager.subscribe((msg: SSEMessageResource) => {
		if (!msg.resource.startsWith("sync:user")) {
			return;
		}
		const scopes = [..._allByScope.value.keys()];
		if (scopes.length === 0) {
			fetchAll();
			return;
		}
		for (const scope of scopes) {
			fetchAll(scope);
		}
	});
	SSEManager.subscribeReconnect(() => {
		const scopes = [..._allByScope.value.keys()];
		_allByScope.value = new Map();
		if (scopes.length === 0) {
			fetchAll();
			return;
		}
		for (const scope of scopes) {
			fetchAll(scope);
		}
	});
	// Actions
	function byID(id: number): User | null {
		const result = _byID.value.get(id);
		if (!result) {
			return null;
		}
		console.log("user", id, result);
		return result;
	}
	async function byURI(uri: string): Promise<User | null> {
		const all = await withAll();
		const result = all.find((u: User) => u.uri == uri);
		return result || null;
	}
	/**
	 * Loads the user list. `allOrganizations` asks the server for every user in
	 * every district; it is an administrator's call (organization
	 * configuration, sudo tools) and the server honours it only for root.
	 * Without it the list is the caller's own district, root included.
	 */
	async function fetchAll(allOrganizations = false): Promise<User[]> {
		const sessionStore = useSessionStore();
		const session = await sessionStore.get();
		loading.value = true;
		error.value = null;
		try {
			const params = new URLSearchParams();
			params.append("sort", "-created");
			if (allOrganizations) {
				params.append("all_organizations", "true");
			}

			const response = await fetch(`${session.urls.api.user}?${params}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const users = await response.json();
			_allByScope.value.set(allOrganizations, users);
			for (const u of users) {
				_byID.value.set(u.id, u);
			}
			return users;
		} catch (err) {
			console.error("Error loading users:", err);
			throw err;
		}
	}
	async function withAll(allOrganizations = false): Promise<User[]> {
		const cached = _allByScope.value.get(allOrganizations);
		if (cached != null) {
			return cached;
		}

		const ongoing = ongoingFetches.get(allOrganizations);
		if (ongoing !== undefined) {
			return ongoing;
		}

		const request = fetchAll(allOrganizations).finally(() => {
			ongoingFetches.delete(allOrganizations);
		});
		ongoingFetches.set(allOrganizations, request);
		return request;
	}
	async function fetchOne(id: number) {
		const session = useSessionStore();
		if (session.urls == null) {
			throw new Error("can't fetch without user URL data");
		}

		loading.value = true;
		error.value = null;
		try {
			const response = await fetch(`${session.urls.api.user}/${id}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data = await response.json();
			_byID.value.set(data.id, data);
			return data;
		} catch (err) {
			console.error("Error loading users:", err);
			throw err;
		}
	}

	return {
		// Actions
		byID,
		byURI,
		fetchAll,
		fetchOne,
		withAll,
	};
});
